import { CreateMooringSiteSetupDto, MooringSiteSetupDto } from './MooringSiteSetup';
import { MooringStationListItem } from './MooringStation';

export const MooringOfferStatus = {
  Playground: 1,
  InProgress: 2,
  Review: 3,
  ReviewApproved: 4,
  RequiresCorrection: 5,
  Sent: 6,
  Accepted: 7,
  Rejected: 8,
  Cancelled: 9,
} as const;

export type MooringOfferStatus = (typeof MooringOfferStatus)[keyof typeof MooringOfferStatus];

export const MooringOfferStatusTranslation: {
  [key in MooringOfferStatus]: string;
} = {
  '1': 'mooring.status.playground',
  '2': 'mooring.status.inProgress',
  '3': 'mooring.status.review',
  '4': 'mooring.status.reviewApproved',
  '5': 'mooring.status.requiresCorrection',
  '6': 'mooring.status.sent',
  '7': 'mooring.status.accepted',
  '8': 'mooring.status.rejected',
  '9': 'mooring.status.cancelled',
};

export const ValidStatusForReview = [MooringOfferStatus.InProgress, MooringOfferStatus.RequiresCorrection, MooringOfferStatus.Playground];

export class MooringOfferListItemDto {
  Id: number;
  Title: string;
  Status: number;
  Created: string;

  Contact?: string;
  ContactId?: number;
  CrmReference?: string;

  Customer?: string;
  CustomerId?: number;

  Locality?: string;
  LocalityId?: number;
  LocalityNumber?: string;

  SalesPerson?: string;
  SalesPersonId: number;
  
  VersionNumber: number;
  TotalVersions: number;
}

export class PersonInfoDto {
  Id: string;
  Name: string;
  Email: string;
}

export class CustomerInfoDto {
  Id: string;
  Name: string;
}

export class MooringOfferDto {
  Id: number;
  Title: string;

  IsLocked: boolean;

  CustomerId: number;
  Customer: CustomerInfoDto;

  ContactId: number;
  Contact: PersonInfoDto;

  SalesPersonId: string;
  SalesPerson: PersonInfoDto;

  ProductionLocationId: number;
  ProductionLocation: MooringStationListItem;

  SiteId: number;

  MooringId: number;
  SiteSetupId: number;

  SiteSetup: MooringSiteSetupDto;

  ActiveReviewId?: number;
  HasAnApprovedReview: boolean;

  SiteNumber: string;
  AnalysisReference: string;

  AnalysisCompanyId: number;
  AnalysisCompanyName: string;

  AnalysisAttachmentId: string;
  CrmReference: string;

  Hs: number;
  Vc: number;

  Status: MooringOfferStatus;
  StatusName: string;

  CreatedAt: Date;

  UpdatedAt: Date;

  AquacomSyncedAt: Date;
  AquacomMooringId: number;
  CanTransferToAquacom: boolean;
  ChangesSinceLastTransferToAquacom: boolean;

  HasOnlyFleet: boolean;
  
  CanSendForReview: boolean;
  CanUnlock: boolean;
  CanTransferToProduction: boolean;
  AvailableStatuses: MooringOfferStatus[];
  
  CurrentVersion: number;
  TotalVersions: number;
}

export class CreateMooringOfferDto {
  CustomerId: number;
  ContactId: number;
  SiteId: number;
  SalesPersonId: string;
  ProductionLocationId: number;
  Title: string;
  SiteNumber: string;
  CrmReference: string;
  AnalysisReference: string;
  AnalysisCompanyId: number;
  Hs: number;
  Vc: number;
  StatusId: number;
  SiteSetup: CreateMooringSiteSetupDto;

  CreateFacility: boolean;
  CreateFleet: boolean;
  CreateOnlyFleet: boolean;

  NoBridlesOld?: number;
  NoBridlesNew?: number
}

export class UpdateMooringOfferDto extends CreateMooringOfferDto {}

export class UpdateMooringOfferStatusDto {
  Status: MooringOfferStatus;
}

export class TransferToAquacomDto {
  OfferId: number;
  CustomerId: number;
  LocalityId: number;
}

export class UpdateInAquacomDto {
  OfferId: number;
  CustomerId: number;
  LocalityId: number;
  MooringAquacomId: number;
}

export class MooringOfferStatusPipelineItem {
  StatusId: number;
  MooringOfferStatus: any;
  MooringOffers: MooringOfferDto[];
}

export class UpdateMooringOfferStatusContentQueueDto {
  MooringOffers: UpdateMooringOfferStatusContentQueueItem[];
}

export class UpdateMooringOfferStatusContentQueueItem {
  MooringOfferId: number;
  SortIndex: number;
}

export class MooringOfferVersionDto {
  Id: number;
  OfferId: number;
  VersionNumber: number;
  MooringId: number;
  Created?: Date;
  CreatedById: string;
  CreatedByName: string;
  IsActiveVersion: boolean;
  
  // Offer metadata snapshot
  Title: string;
  StatusId: number;
  StatusName: string;
  ContactId?: number;
  ContactName?: string;
  SiteId: number;
  SiteName: string;
  SiteNumber: string;
  CrmReference: string;
  AnalysisReference: string;
  AnalysisCompanyId: number;
  AnalysisCompanyName: string;
  Hs: number;
  Vc: number;
  SalesPersonId: string;
  SalesPersonName: string;
  ProductionLocationId?: number;
  ProductionLocationName?: string;
}

export class MooringExtraArticleItemDto {
  Id: number;
  Quantity: number;
  Comment?: string;
}

export class MooringOfferBomLineDto {
  MooringArticleProductId: number;
  ProductName: string;
  ArticleNumber: string;
  Category: string;
  ProductQuantity: number;
  Quantity: number;
  UnitSize: number;
  ExtraQuantity: number;
  TotalQuantity: number;
  IsExtraArticle: boolean;
  ExtraArticles: MooringExtraArticleItemDto[] = [];
}

export class MooringOfferBomGroupDto {
  Category: string;
  Items: MooringOfferBomLineDto[] = [];
}

export class MooringOfferBomDto {
  GroupedByCategory: boolean;
  Items: MooringOfferBomLineDto[] = [];
  Groups: MooringOfferBomGroupDto[] = [];
}

export class CreateMooringExtraArticleDto {
  MooringArticleProductId: number;
  Quantity: number;
  Comment?: string;
}

export class UpdateMooringExtraArticleDto {
  Quantity: number;
  Comment?: string;
}

export class MooringOfferChangeDifferenceDto {
  NoCagesWidthOld?: number;
  NoCagesWidthNew?: number;
  NoCagesHeightOld?: number;
  NoCagesHeightNew?: number;
  NoBridlesOld?: number;
  NoBridlesNew?: number;
  NoAnchorLinesOld?: number;
  NoAnchorLinesNew?: number;
}

export class MooringOfferChangeImpactDto {
  CagesAdded?: number;
  CagesRemoved?: number;
  BridlesAdded?: number;
  BridlesRemoved?: number;
  AnchorLinesAdded?: number;
  AnchorLinesRemoved?: number;
  BuoysAdded?: number;
  BuoysRemoved?: number;
  GridLinesAdded?: number;
  GridLinesRemoved?: number;
}
